import styled from "@emotion/styled";
import classNames from "classnames";
import { ellipsis } from "polished";
import { FC, ReactNode } from "react";

import { Avatar, DesignToken, Text } from "@livechat/design-system-react-components";

import Flex from "components/layout/Flex";
import { RoleTag } from "components/role-tag/RoleTag";
import { EmailBounced, IAgent } from "store/entities/agents";
import { textEllipsisStyles } from "styles/common";
import { withVar } from "themes/helpers";

import { AutoassignmentStatus } from "./AutoassignmentStatus";
import { InvitationStatus } from "./InvitationStatus";
import { ColumnWidths } from "./styled";

interface IProps {
  selected: boolean;
  agent: IAgent;
  isEmailBounced?: EmailBounced;
  onSelectAgent(agentId: string): void;
  onDoubleClickAgent(agentId: string): void;
}

export const AgentRow: FC<IProps> = ({ selected, agent, onSelectAgent, onDoubleClickAgent }) => {
  const handleSelectAgent = (): void => {
    onSelectAgent(agent.id);
  };

  const handleDoubleClickAgent = (): void => {
    onDoubleClickAgent(agent.id);
  };

  return (
    <tr
      className={classNames("selectable", {
        selected,
      })}
      onClick={handleSelectAgent}
      onDoubleClick={handleDoubleClickAgent}
    >
      <td style={{ width: ColumnWidths.Avatar }}>
        <div className="selected-mark" />
        <Flex justifyContent="flex-end">
          <Avatar
            type={agent.avatar ? "image" : "text"}
            src={agent.avatar ? agent.avatar : undefined}
            text={agent.name ?? agent.email}
            size="large"
          />
        </Flex>
      </td>
      <AgentTableData>
        <AgentName>{agent.name}</AgentName>
        <AgentEmail size="sm">{agent.email}</AgentEmail>
      </AgentTableData>
      <AgentTableData width={ColumnWidths.Role}>
        <RoleTag role={agent.permission} />
      </AgentTableData>
      <AgentTableData width={ColumnWidths.AutoAssignment}>
        <AutoassignmentStatus agent={agent} />
      </AgentTableData>
      <AgentTableData width={ColumnWidths.InvitationStatus}>
        <InvitationStatus agent={agent} />
      </AgentTableData>
    </tr>
  );
};

interface IAgentTableDataProps {
  width?: string;
  children: ReactNode;
  className?: string;
}

const AgentTableData: FC<IAgentTableDataProps> = ({ width, children, className }) => (
  <td style={{ width }} className={className}>
    <TableDataContainer>{children}</TableDataContainer>
  </td>
);

const TableDataContainer = styled.div`
  padding-inline: 18px;
`;

const AgentName = styled(Text)`
  color: ${withVar(DesignToken.ContentBasicPrimary)};
  ${ellipsis()};
`;

const AgentEmail = styled(Text)`
  color: ${withVar(DesignToken.ContentBasicSecondary)};
  ${textEllipsisStyles};
`;
